"use client";

import { checkCountryCode, COUNTRY_CODE_KEY, CountryCode, countryList, europeList, getInitialCountryCode } from "@/00-shared/const";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/03-presenters/components/ui/select";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { create } from "zustand";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Portal } from "@radix-ui/react-tooltip";
import { cx } from "class-variance-authority";
import { useUserInfo } from "@/app/[locale]/hooks";
const disableConfig: Record<string, {
  countryList: CountryCode[];
  tips?: string;
}> = {
  "/aiReport": {
    countryList: europeList,
    tips: "该国家暂未开放该模块"
  },
  "/tiktok/dashboard/agency-dashboard": {
    countryList: [CountryCode.TH, CountryCode.VN, CountryCode.MY, CountryCode.MX, ...europeList, CountryCode.JP],
    tips: "该国家暂未开放该模块"
  },
  "/tiktok/entity/tiktok_product/product_history_view": {
    countryList: [CountryCode.TH, CountryCode.VN, CountryCode.MY, CountryCode.MX, CountryCode.JP, ...europeList],
    tips: "该国家暂未开放该模块"
  },
  "/tiktok/dashboard/category-dashboard": {
    countryList: [CountryCode.JP],
    tips: "该国家暂未开放该模块"
  },
  "/tiktok/dashboard/product-dashboard": {
    countryList: [CountryCode.JP],
    tips: "该国家暂未开放该模块"
  },
  "/tiktok/dashboard/seller-dashboard": {
    countryList: [CountryCode.JP],
    tips: "该国家暂未开放该模块"
  }
};
const disablePathList = Object.keys(disableConfig);
function getDisableConfig(pathName: string) {
  for (const path of disablePathList) {
    if (pathName.startsWith(path)) {
      return disableConfig[path];
    }
  }
  return {
    countryList: []
  };
}
export const countryStore = create<{
  countryCode: string;
  disabled: boolean;
  setCountryCode: (code: string) => void;
  setDisabled: (disabled: boolean) => void;
}>(set => ({
  countryCode: getInitialCountryCode(),
  disabled: false,
  setCountryCode: code => {
    set({
      countryCode: code
    });
    const url = new URL(window.location.href);
    url.searchParams.set(COUNTRY_CODE_KEY, code);
    window.sessionStorage.setItem(COUNTRY_CODE_KEY, code);
    window.history.replaceState(null, "", url);
  },
  setDisabled: disabled => {
    set({
      disabled
    });
  }
}));
export default function MultiCountrySelect() {
  const pathName = usePathname();
  const disabled = countryStore(state => state.disabled);
  const countryCode = countryStore(state => state.countryCode);
  const setCountryCode = countryStore(state => state.setCountryCode);
  const disableCountryList = getDisableConfig(pathName)?.countryList || [];
  const imageUrl = `/images/flag/${countryCode.toLowerCase()}.png`;
  const countryName = countryList.find(country => country.code === countryCode)?.name;
  // const { getIsGray } = useUserInfo();
  // const isGray = getIsGray("jp");
  // const filteredCountryList = countryList.filter((country) => {
  //   if (country.code === CountryCode.JP) {
  //     return isGray;
  //   }
  //   return true;
  // });
  if (pathName.startsWith("/tiktok/detail") || pathName.startsWith("/tiktok/dashboard/agency-detail") || pathName.startsWith("/aiToolV2/inspiration/task") || pathName.startsWith("/inspiration/detail") || pathName.startsWith("/aiReport/")) {
    return <div className="flex items-center justify-center gap-2 rounded-lg border-1 border-solid border-gray-3 px-2 py-1">
        <div className="relative h-5 w-5">
          <Image className="object-contain" src={imageUrl} fill alt="flag" />
        </div>
        <div className="text-nowrap text-[14px]">{countryName}</div>
      </div>;
  }
  return <Select value={countryCode} onValueChange={setCountryCode} data-sentry-element="Select" data-sentry-component="MultiCountrySelect" data-sentry-source-file="MultiCountrySelect.tsx">
      <SelectTrigger className="flex h-8 w-fit items-center justify-center gap-1 px-2" data-sentry-element="SelectTrigger" data-sentry-source-file="MultiCountrySelect.tsx">
        <div className="relative h-4 w-4">
          <Image className="object-contain" src={imageUrl} fill alt="flag" data-sentry-element="Image" data-sentry-source-file="MultiCountrySelect.tsx" />
        </div>
        <SelectValue data-sentry-element="SelectValue" data-sentry-source-file="MultiCountrySelect.tsx">{countryName}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-[101]" data-sentry-element="SelectContent" data-sentry-source-file="MultiCountrySelect.tsx">
        {countryList.map(country => {
        const defaultDisable = disableCountryList.includes(country.code as CountryCode);
        const itemDisabled = (defaultDisable || disabled) && country.code !== countryCode;
        const tips = itemDisabled ? getDisableConfig(pathName)?.tips : undefined;
        const countryImageUrl = `/images/flag/${country.code.toLowerCase()}.png`;
        const isSelected = country.code === countryCode;
        return <SelectItem key={country.code} value={country.code} disabled={itemDisabled} showCheck={false} className={cx("pl-2", isSelected && "bg-primary text-white focus:bg-primary focus:text-white")}>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex w-full items-center gap-2 ">
                      <div className="relative h-4 w-4">
                        <Image className="object-contain" src={countryImageUrl} fill alt="flag" />
                      </div>
                      <span>{country.name}</span>
                      {[CountryCode.JP].includes(country.code as CountryCode) && <div className="rounded-sm bg-primary px-1.5 text-[10px] text-white">
                          公测
                        </div>}
                    </div>
                  </TooltipTrigger>
                  <Portal>
                    <TooltipContent className={cx(tips ? "z-[101] block" : "hidden")}>
                      {tips}
                    </TooltipContent>
                  </Portal>
                </Tooltip>
              </TooltipProvider>
            </SelectItem>;
      })}
      </SelectContent>
    </Select>;
}